const front =
  typeof window !== 'undefined' && window.__ZOOPUP_FRONT__ ? window.__ZOOPUP_FRONT__ : {};

function trimSlash(value) {
  return String(value || '').replace(/\/+$/, '');
}

export function appBaseUrl() {
  if (front.appBaseUrl) {
    return trimSlash(front.appBaseUrl);
  }

  const dashboard = front.dashboardUrl || '';
  if (!dashboard) {
    return '';
  }

  try {
    const url = new URL(
      dashboard,
      typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
    );
    return trimSlash(
      url.pathname.endsWith('/dashboard')
        ? `${url.origin}${url.pathname.replace(/\/dashboard\/?$/, '')}`
        : url.origin
    );
  } catch (error) {
    return trimSlash(dashboard.replace(/\/dashboard\/?$/, ''));
  }
}

export function appUrl(path = '/') {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const base = appBaseUrl();

  if (!base) {
    return normalized;
  }

  try {
    return new URL(normalized, `${base}/`).toString();
  } catch (error) {
    return `${base}${normalized}`;
  }
}

export function appLoginUrl(redirectPath = null) {
  const login = front.loginUrl || '/login';
  if (!redirectPath) {
    return login;
  }

  const path = redirectPath.startsWith('/') ? redirectPath : `/${redirectPath}`;
  const joiner = login.includes('?') ? '&' : '?';
  return `${login}${joiner}redirect=${encodeURIComponent(path)}`;
}

export function appSignupUrl() {
  return front.signupUrl || '/signup/intent';
}

export function appDashboardUrl() {
  return front.dashboardUrl || '/dashboard';
}

export function appProjectsCreateUrl() {
  return front.projectsCreateUrl || appUrl('/projects/create');
}

export function appMicroJobCheckoutUrl(id) {
  if (front.microJobCheckoutUrlTemplate && id != null) {
    return String(front.microJobCheckoutUrlTemplate).replace(':id', encodeURIComponent(id));
  }
  return appUrl(`/microjobs/${id}`);
}

export function appMicroJobOrdersUrl() {
  return front.microJobOrdersUrl || appUrl('/microjobs/orders');
}

export function appFreelancerProjectsUrl() {
  return front.freelancerProjectsUrl || appUrl('/freelancer/projects');
}

export function marketingHomeUrl() {
  const url = front.marketingUrl || '/';

  if (typeof window === 'undefined') {
    return url;
  }

  try {
    const target = new URL(url, window.location.origin);
    if (target.origin === window.location.origin) {
      return `${target.pathname}${target.search}${target.hash}` || '/';
    }
  } catch (error) {
    return url;
  }

  return url;
}

export const appAuthLinkAttrs = {
  target: '_blank',
  rel: 'noopener noreferrer',
};

/** Same-tab navigation into the transactional app portal. */
export const appPortalLinkAttrs = {
  target: '_self',
  rel: 'noopener noreferrer',
};

export function shouldUseExternalAppAuth(url) {
  if (!url) {
    return false;
  }

  try {
    return new URL(url, window.location.origin).origin !== window.location.origin;
  } catch (error) {
    return false;
  }
}

export function redirectIfExternalAppAuth(getUrl) {
  return (_to, _from, next) => {
    const url = typeof getUrl === 'function' ? getUrl() : getUrl;

    if (shouldUseExternalAppAuth(url) || (url && !url.startsWith('/'))) {
      window.location.assign(url);
      return next(false);
    }

    return next();
  };
}

export function redirectToAppUrl(getUrl) {
  return (_to, _from, next) => {
    const url = typeof getUrl === 'function' ? getUrl() : getUrl;
    window.location.assign(url);
    return next(false);
  };
}

export function normalizeSignupUrl(url, fallback = null) {
  if (!url) {
    return fallback || appSignupUrl();
  }

  try {
    const path = new URL(url, window.location.origin).pathname.toLowerCase().replace(/\/$/, '');
    if (
      /(^|\/)(account\/register|accountregister)$/.test(path) &&
      shouldUseExternalAppAuth(appSignupUrl())
    ) {
      return appSignupUrl();
    }
  } catch (error) {
    return url;
  }

  return url;
}
